import moment from "moment";
import { AnyAction, Reducer } from "redux";
import { IAppThunkAction } from ".";
import TimerApi from "../api/TimerApi";
import { AppInsightsLogger } from "../components/shared/logging/AppInsightsLogger";
import Constants from "../models/utility/Constants";

export interface ITimerStoreState {
    interval: number | null;
    // The count is not useful, but its change will trigger a UI refresh
    count: number;
    clientTimeOffsetInMilliseconds: number;
}

export type KnownAction = ISetClientTimeOffset | ISetInterval | ITick;

export interface ISetClientTimeOffset {
    type: "SET_CLIENT_TIMER_OFFSET";
    offset: number;
}
export interface ISetInterval {
    type: "SET_INTERVAL";
    interval: number | null;
}
export interface ITick {
    type: "TICK";
}

export const actionCreators = {
    getServerTime: (): IAppThunkAction => async (dispatch) => {
        const performanceBefore = performance.now();
        const serverTimeResponse = await TimerApi.GetServerTime();
        const performanceAfter = performance.now();

        AppInsightsLogger.trackEvent({
            name: `Call duration in milliseconds: ${
                performanceAfter - performanceBefore
            }`
        });

        if (serverTimeResponse) {
            const offset = moment(new Date(serverTimeResponse.now)).diff(
                moment(new Date())
            );
            AppInsightsLogger.trackEvent({
                name: `Clock offset (Server Time - Client Time): ${offset}`
            });
            dispatch({ type: "SET_CLIENT_TIMER_OFFSET", offset });
        }
    },
    startClientInterval:
        (fn: TimerHandler): IAppThunkAction =>
        (dispatch, getState) => {
            const prevInterval = getState().timerStoreState.interval;
            if (prevInterval) {
                actionCreators.stopClientInterval();
            }

            const interval = setInterval(fn, Constants.ClientTickLength);
            dispatch({ type: "SET_INTERVAL", interval });
        },
    stopClientInterval: (): IAppThunkAction => (dispatch, getState) => {
        let interval = getState().timerStoreState.interval;
        if (interval) {
            clearInterval(interval);
        }

        interval = null;
        dispatch({ type: "SET_INTERVAL", interval });
    },
    tickClientClocks: (): IAppThunkAction => (dispatch) => {
        dispatch({ type: "TICK" });
    }
};

export const reducer: Reducer<ITimerStoreState> = (
    state: ITimerStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "SET_CLIENT_TIMER_OFFSET": {
            return {
                ...prevState,
                clientTimeOffsetInMilliseconds:
                    Math.abs(action.offset) >= Constants.ClientTimerOffsetBuffer
                        ? action.offset
                        : 0
            };
        }
        case "SET_INTERVAL": {
            return {
                ...prevState,
                interval: action.interval
            };
        }
        case "TICK": {
            return {
                ...prevState,
                count: prevState.count + 1
            };
        }
        default: {
            break;
        }
    }

    return prevState;
};

export const defaultState = {
    count: 0,
    interval: null
} as ITimerStoreState;
